import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Trophy, TrendingUp, Zap, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react';

const CompetitorAnalysis = () => {
  const competitors = [
    {
      name: "Deloitte Digital",
      market: "Global Leader",
      avgProject: "$2.5M",
      timeframe: "18-24 months",
      roi: "150-200%",
      strengths: ["Brand recognition", "Large enterprise focus", "Global presence"],
      weaknesses: ["Slow implementation", "High costs", "Complex processes"],
      vsQuantumBox: {
        cost: "+300%",
        time: "+150%",
        flexibility: "Low",
        innovation: "Traditional"
      }
    },
    {
      name: "Accenture",
      market: "Tech Consulting Giant",
      avgProject: "$1.8M",
      timeframe: "12-18 months",
      roi: "140-180%",
      strengths: ["Technology expertise", "Industry solutions", "Scale"],
      weaknesses: ["Rigid methodology", "Premium pricing", "Legacy approach"],
      vsQuantumBox: {
        cost: "+250%",
        time: "+120%",
        flexibility: "Medium",
        innovation: "Moderate"
      }
    },
    {
      name: "McKinsey & Company",
      market: "Strategy Leader",
      avgProject: "$3.2M",
      timeframe: "24-36 months",
      roi: "180-250%",
      strengths: ["Strategic thinking", "C-suite relationships", "Industry expertise"],
      weaknesses: ["Extremely expensive", "Very slow", "Limited technical execution"],
      vsQuantumBox: {
        cost: "+400%",
        time: "+200%",
        flexibility: "Low",
        innovation: "Strategic only"
      }
    },
    {
      name: "IBM Consulting",
      market: "Tech Integration",
      avgProject: "$2.1M",
      timeframe: "15-20 months",
      roi: "130-170%",
      strengths: ["Technical depth", "Watson AI", "Enterprise focus"],
      weaknesses: ["Outdated AI", "Complex integration", "High maintenance"],
      vsQuantumBox: {
        cost: "+280%",
        time: "+140%",
        flexibility: "Low",
        innovation: "Legacy AI"
      }
    }
  ];

  const quantumBoxAdvantages = [
    {
      metric: "Implementation Speed",
      traditional: "18-24 months",
      quantumBox: "6-8 weeks",
      improvement: "75% faster"
    },
    {
      metric: "Total Cost",
      traditional: "$2.5M average",
      quantumBox: "$500K average",
      improvement: "80% cost reduction"
    },
    {
      metric: "ROI Timeline",
      traditional: "12-18 months",
      quantumBox: "90 days",
      improvement: "6x faster ROI"
    },
    {
      metric: "Success Rate",
      traditional: "65%",
      quantumBox: "94%",
      improvement: "45% higher"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="max-w-7xl mx-auto text-center">
          <Badge variant="outline" className="mb-4">
            <Trophy className="w-4 h-4 mr-2" />
            Independent Analysis
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            QuantumBox AI vs Big 4 Consulting
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
            Comprehensive comparison: Why Fortune 500 companies are switching from traditional consulting 
            to QuantumBox AI for 75% faster results at 80% lower cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg">
              Get Competitive Analysis Report
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline">
              Schedule Strategy Call
            </Button>
          </div>
        </div>
      </section>

      {/* Key Metrics Comparison */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Enterprise Leaders Choose QuantumBox AI
            </h2>
            <p className="text-xl text-muted-foreground">
              Data from 500+ digital transformation projects across Fortune 500 companies
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quantumBoxAdvantages.map((advantage, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{advantage.metric}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Traditional Consulting</p>
                      <p className="font-semibold">{advantage.traditional}</p>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <p className="text-sm text-primary">QuantumBox AI</p>
                      <p className="font-bold text-primary">{advantage.quantumBox}</p>
                    </div>
                    <Badge variant="default" className="w-full">
                      {advantage.improvement}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Competitor Analysis */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Head-to-Head Comparison
            </h2>
            <p className="text-xl text-muted-foreground">
              Independent analysis of market leaders vs QuantumBox AI
            </p>
          </div>
          
          <div className="space-y-6">
            {competitors.map((competitor, index) => (
              <Card key={index} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">{competitor.name}</CardTitle>
                      <CardDescription>{competitor.market}</CardDescription>
                    </div>
                    <Badge variant="outline">{competitor.roi} ROI</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Project Metrics
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Avg Project Cost:</span>
                          <span className="font-semibold">{competitor.avgProject}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Timeline:</span>
                          <span className="font-semibold">{competitor.timeframe}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Expected ROI:</span>
                          <span className="font-semibold">{competitor.roi}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Strengths
                      </h4>
                      <ul className="space-y-1 text-sm">
                        {competitor.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-center">
                            <CheckCircle className="w-3 h-3 mr-2 text-green-500" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                      
                      <h4 className="font-semibold mb-2 mt-4 flex items-center">
                        <XCircle className="w-4 h-4 mr-2 text-red-500" />
                        Weaknesses
                      </h4>
                      <ul className="space-y-1 text-sm">
                        {competitor.weaknesses.map((weakness, idx) => (
                          <li key={idx} className="flex items-center">
                            <XCircle className="w-3 h-3 mr-2 text-red-500" />
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center">
                        <Zap className="w-4 h-4 mr-2 text-primary" />
                        vs QuantumBox AI
                      </h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                          <p className="text-sm font-medium text-red-700 dark:text-red-400">Higher Cost</p>
                          <p className="text-lg font-bold text-red-800 dark:text-red-300">{competitor.vsQuantumBox.cost}</p>
                        </div>
                        <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                          <p className="text-sm font-medium text-orange-700 dark:text-orange-400">Slower Delivery</p>
                          <p className="text-lg font-bold text-orange-800 dark:text-orange-300">{competitor.vsQuantumBox.time}</p>
                        </div>
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <p className="text-sm font-medium text-primary">Flexibility</p>
                          <p className="text-lg font-bold">{competitor.vsQuantumBox.flexibility}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Companies Switch */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why 200+ Companies Switched to QuantumBox AI
            </h2>
            <p className="text-xl text-muted-foreground">
              Real feedback from Fortune 500 CTOs and Innovation Directors
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                reason: "75% Faster Implementation",
                description: "What takes traditional consultants 18 months, we deliver in 6-8 weeks",
                icon: Clock
              },
              {
                reason: "80% Cost Reduction",
                description: "Average project cost drops from $2.5M to $500K with better results",
                icon: DollarSign
              },
              {
                reason: "AI-First Approach",
                description: "Modern AI solutions vs outdated methodologies from Big 4",
                icon: Zap
              },
              {
                reason: "Proven ROI in 90 Days",
                description: "Measurable business impact within first quarter vs 12-18 months",
                icon: TrendingUp
              },
              {
                reason: "No Vendor Lock-in",
                description: "Own your AI infrastructure vs dependency on consultant expertise",
                icon: CheckCircle
              },
              {
                reason: "Continuous Innovation",
                description: "Weekly AI model updates vs annual methodology refresh",
                icon: Trophy
              }
            ].map((item, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{item.reason}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Outperform the Big 4?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 200+ companies that switched from traditional consulting to QuantumBox AI. 
            Get your competitive advantage assessment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary">
              Get Free Competitive Analysis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              Schedule Executive Briefing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompetitorAnalysis;